import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { SEO, StructuredData } from '@/components/seo'
import { PricingGrid } from '@/components/sections/PricingGrid'

export default function PricingPage() {
  const pricingFaqs = [
    {
      question: 'Do you charge per seat?',
      answer: 'No. Collaboration improves hiring decisions — we don\'t penalize it.',
    },
    {
      question: 'Do you charge per interview or per resume?',
      answer: 'No. That encourages rushing or cutting corners.',
    },
    {
      question: 'Can we downgrade when hiring slows down?',
      answer: 'Yes. Pricing reflects active hiring, not headcount.',
    },
  ]

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Pricing - Plans for Every Team Size"
        description="JumpstoneHR pricing that reflects how hiring actually feels. No per-seat or per-resume charges. Free tier available. Scale up when hiring gets busy, scale down when it slows."
        canonical="/pricing"
        structuredData={[
          StructuredData.webPage(
            'JumpstoneHR Pricing',
            'Flexible pricing plans for AI-powered hiring. Free starter tier, team plans, and enterprise solutions.',
            '/pricing'
          ),
          StructuredData.breadcrumb([
            { name: 'Home', url: '/' },
            { name: 'Pricing', url: '/pricing' },
          ]),
          StructuredData.faq(pricingFaqs),
          {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: 'JumpstoneHR',
            description: 'AI-powered hiring decision platform',
            offers: [
              {
                '@type': 'Offer',
                name: 'Starter',
                price: '0',
                priceCurrency: 'CAD',
                description: 'Free tier for founders hiring their first few people',
              },
              {
                '@type': 'Offer',
                name: 'Team',
                description: 'For growing teams with multiple roles and candidates',
                priceSpecification: {
                  '@type': 'PriceSpecification',
                  priceCurrency: 'CAD',
                },
              },
              {
                '@type': 'Offer',
                name: 'Pro',
                description: 'For teams at scale with level calibration and audit needs',
                priceSpecification: {
                  '@type': 'PriceSpecification',
                  priceCurrency: 'CAD',
                },
              },
              {
                '@type': 'Offer',
                name: 'Enterprise',
                description: 'Custom pricing for agencies and large organizations',
                priceSpecification: {
                  '@type': 'PriceSpecification',
                  priceCurrency: 'CAD',
                },
              },
            ],
          },
        ]}
      />
      <Header />
      <main className="pt-16">
        {/* Hero */}
        <section className="py-20 bg-[#fafafa]">
          <div className="mx-auto max-w-4xl section-padding text-center">
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-surface-900 mb-6">
              Pricing that reflects how hiring actually feels
            </h1>
            <p className="text-xl text-surface-600 max-w-2xl mx-auto">
              Pay for quality without complexity, not for seats, pipelines, or resumes.<br /> No long-term commitments.<br /> Downscale any time.
            </p>
          </div>
        </section>

        {/* Plans */}
        <section className="py-20 bg-white">
          <div className="mx-auto max-w-7xl section-padding">
            <h2 className="text-3xl font-bold text-surface-900 mb-12 text-center">
              Plans
            </h2>
            <PricingGrid />
          </div>
        </section>

        {/* AI transparency */}
        <section className="py-16 bg-surface-50">
          <div className="mx-auto max-w-3xl section-padding">
            <h2 className="text-2xl font-bold text-surface-900 mb-6 text-center">
              Auditable AI
            </h2>
            <div className="card p-8">
              <p className="text-surface-700 mb-4">
                AI's role is to:
              </p>
              <ul className="text-surface-600 mb-6 space-y-3">
                {['Extract signal from evidence', 'Apply the same lens to every candidate', 'Support (not replace) human judgment'].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-surface-700 mb-4">
                You always see:
              </p>
              <ul className="text-surface-600 mb-6 space-y-3">
                {['Who evaluated what', 'Why a recommendation was made', 'How confident the system is'].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-surface-50">
          <div className="mx-auto max-w-3xl section-padding text-center">
            <h2 className="text-2xl font-bold text-surface-900 mb-4">
              Ready to make hiring decisions you can trust?
            </h2>
            <p className="text-surface-600 mb-8">
              Start free. Scale when you need to. No long-term commitments. Downscale any time.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/signup"
                className="btn bg-brand-600 text-white hover:bg-brand-700 px-8 py-3"
              >
                Get Started
              </a>
              <a
                href="/contact"
                className="btn bg-white text-surface-900 hover:bg-surface-100 border border-surface-300 px-8 py-3"
              >
                Talk to Us
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}
